import { useMutation } from '@apollo/client'

import {
  CommonBlockIcon,
  CommonDeleteIcon,
  CommonEditIcon,
  CommonReportIcon,
  CommonShareIcon,
  CommonVisitIcon,
} from '@local/assets/icons'

import ContextMenu from '@web/components/ContextMenu/ContextMenu'
import { MenuItemProps } from '@web/components/ContextMenu/MenuItem'
import Alert from '@web/components/Modal/Alert'
import { useModal } from '@web/components/Modal/useModal'
import SocialPlugin from '@web/components/SocialPlugin'
import { openErrorToast } from '@web/components/Toast/GlobalToastPortal'
import { gql } from '@web/gql'
import {
  ContextMenuReview_ReviewGroupFragment,
  BaseError,
  UserReviewGroupsOutput,
  ReviewGroupExposeStatus,
  PlaceOtherReviewGroupsOutput,
} from '@web/gql/graphql'
import useBlockModal from '@web/hooks/useBlockModal'
import { sendCacheAsyncMessage } from '@web/hooks/useGlobalCacheAsync'
import { useGoTo } from '@web/hooks/useGoTo'
import { useHandleClickReviewWrite } from '@web/hooks/useHandleClickReviewWrite'
import useMe from '@web/hooks/useMe'
import { extractByTypename } from '@web/libs/apollo/typeGuard'
import { messages } from '@web/libs/constants'
import links from '@web/libs/constants/links'
import routes from '@web/libs/constants/routes'
import translate from '@web/libs/translate/translate'
import { extractErrorMessage, generatePath, neloLogger } from '@web/libs/utils'
import { ReviewDetailType } from '@web/types'

import styles from './ContextMenuReview.module.scss'

interface Props {
  reviewGroup: ContextMenuReview_ReviewGroupFragment
  nClickArea: {
    SHARE: string
    VIEWMORE: string
    DELETENO?: string
    DELETEYES?: string
    REPORT?: string
    VIEWVISIT?: string
    EDIT?: string
    DELETE?: string
    BLOCK?: string
  }
  redirectToReviewHome?: boolean
  type?: ReviewDetailType
}

const mutation = gql(`
  mutation ContextMenuReview_DeleteReviewGroup($input: DeleteReviewGroupInput!) {
    deleteReviewGroup(input: $input) {
      __typename
      ... on DeleteReviewGroup_Succeed {
        id
      }
      ... on BaseError {
        message
      }
    }
  }
`)

const ContextMenuReview = ({
  reviewGroup,
  nClickArea,
  redirectToReviewHome,
  type = ReviewDetailType.REVIEW_END,
}: Props) => {
  const { goTo, goToLogin, goToReviewEditByReviewGroup } = useGoTo()
  const { t } = translate({ contextType: 'reviewWrite' })
  const { me, login } = useMe()

  // TODO: KnownError로 수정 예정
  if (!reviewGroup.author) throw Error(`[ContextMenuReview] 리뷰 작성자가 없습니다 리뷰그룹ID: ${reviewGroup.id}`)

  const { openBlockModal, openUnBlockModal } = useBlockModal({ user: reviewGroup.author })

  const [deleteReviewGroup] = useMutation(mutation, {
    onCompleted({ deleteReviewGroup }) {
      const { DeleteReviewGroup_Succeed } = extractByTypename(deleteReviewGroup)
      if (!DeleteReviewGroup_Succeed) {
        openErrorToast(t('myplace_review_write_toast_error_delete'))
        neloLogger(
          'error',
          `${t('myplace_review_write_toast_error_delete')}, ${(deleteReviewGroup as BaseError).message}`,
        )
        return
      }

      if (reviewGroup.visit) sendCacheAsyncMessage({ type: 'timeline', id: reviewGroup.visit.id })

      if (!redirectToReviewHome) return

      goTo(routes.HOME_MY_REVIEW)
    },
    onError(error) {
      openErrorToast(t('myplace_review_write_toast_error_delete'))
      neloLogger('error', `${t('myplace_review_write_toast_error_delete')}, ${extractErrorMessage(error)}`)
    },
    update(cache, { data }) {
      const { DeleteReviewGroup_Succeed } = extractByTypename(data?.deleteReviewGroup)
      if (!DeleteReviewGroup_Succeed) return

      cache.modify({
        fields: {
          placeOtherReviewGroups(existing, { readField }) {
            const { ReviewGroupConnection } = extractByTypename(existing as PlaceOtherReviewGroupsOutput)
            if (!ReviewGroupConnection) return existing
            return {
              ...ReviewGroupConnection,
              edges: ReviewGroupConnection.edges.filter(
                edge => readField('id', edge.reviewGroup) !== DeleteReviewGroup_Succeed.id,
              ),
            }
          },
          userReviewGroups(existing, { readField }) {
            const { UserReviewGroupsConnection } = extractByTypename(existing as UserReviewGroupsOutput)
            if (!UserReviewGroupsConnection) return existing
            return {
              ...UserReviewGroupsConnection,
              edges: UserReviewGroupsConnection.edges.filter(
                reviewRef => readField('id', reviewRef) !== DeleteReviewGroup_Succeed.id,
              ),
            }
          },
        },
      })
    },
  })

  const { openModal: openDeleteReviewModal } = useModal(Alert, {
    label: {
      confirm: '삭제',
      cancel: '취소',
    },
    title: '리뷰 삭제',
    message: '리뷰를 삭제하시겠습니까?',
    clickCodes: {
      cancel: nClickArea?.DELETENO,
      confirm: nClickArea?.DELETEYES,
    },
    callback: {
      confirm: () =>
        deleteReviewGroup({
          variables: {
            input: {
              id: reviewGroup.id,
            },
          },
        }),
    },
  })

  const handleReviewEditClick = useHandleClickReviewWrite({
    callback: () => goToReviewEditByReviewGroup({ reviewGroup, openWindow: type === ReviewDetailType.REVIEW_FEED }),
    blockUnavailable: true,
  }) as any

  const isMyReview = me?.id === reviewGroup.author?.id

  const reviewDetailLink = `${links.MY_HOME}${generatePath(routes.REVIEW_DETAIL, {
    reviewGroupId: reviewGroup.id ?? '',
  })}`

  const socialPlugin = (
    <SocialPlugin key="review-context-social" url={reviewDetailLink} clickCode={nClickArea?.SHARE}>
      공유하기
    </SocialPlugin>
  )

  const goToReportReview = () => {
    if (!login) {
      goToLogin({ showAlert: true })
    } else {
      goTo(routes.REVIEW_REPORT, { params: { reviewGroupId: reviewGroup.id } })
    }
  }

  const onClickBlockUser = () => {
    if (!login) {
      goToLogin({ showAlert: true })
    } else {
      openBlockModal()
    }
  }

  const items: MenuItemProps[] = [
    {
      label: socialPlugin,
      icon: CommonShareIcon,
      clickCode: nClickArea?.SHARE,
      visibility: reviewGroup.exposeStatus === ReviewGroupExposeStatus.Expose,
    },
    {
      label: '리뷰 신고',
      icon: CommonReportIcon,
      clickHandler: goToReportReview,
      clickCode: nClickArea?.REPORT,
      visibility: !isMyReview,
    },
    {
      label: '리뷰어 차단',
      icon: CommonBlockIcon,
      clickHandler: onClickBlockUser,
      clickCode: nClickArea?.BLOCK,
      visibility: !isMyReview && !reviewGroup.author?.isBlocked,
    },
    {
      label: '차단 해제',
      icon: CommonBlockIcon,
      clickHandler: () => openUnBlockModal(),
      visibility: !isMyReview && reviewGroup.author?.isBlocked,
    },
    {
      label: '방문 기록 보기',
      icon: CommonVisitIcon,
      clickHandler: () => {
        if (!reviewGroup.visit?.id) return openErrorToast(messages.NOT_FOUND_VISIT)

        return goTo(routes.VISIT_DETAIL, {
          params: { visitId: reviewGroup.visit.id },
          openWindow: type === ReviewDetailType.REVIEW_FEED,
        })
      },
      clickCode: nClickArea?.VIEWVISIT,
      visibility: isMyReview && !!reviewGroup.visit?.id, // TODO: 게시중단(임시/영구) 일 때 방문기록보기 필요할지 논의
    },
    {
      label: '수정하기',
      icon: CommonEditIcon,
      clickHandler: e => handleReviewEditClick(e),
      clickCode: nClickArea?.EDIT,
      visibility:
        isMyReview &&
        [ReviewGroupExposeStatus.Expose, ReviewGroupExposeStatus.ExposeWithNoti].includes(reviewGroup.exposeStatus),
    },
    {
      label: '삭제하기',
      icon: CommonDeleteIcon,
      clickHandler: () => openDeleteReviewModal(),
      clickCode: nClickArea?.DELETE,
      visibility: isMyReview,
    },
  ]

  return (
    <ContextMenu
      button={{
        alt: '리뷰 내역 관리',
        styleClass: styles.btn_manage,
        clickCode: nClickArea?.VIEWMORE,
        disabled: false,
      }}
      layerStyleClass={styles.ly_manage}
      items={items}
    />
  )
}

ContextMenuReview.fragments = {
  reviewGroup: gql(`
    fragment ContextMenuReview_ReviewGroup on ReviewGroup {
      id
      author {
        id
        profile {
          nickname
        }
        isBlocked
        ...UseBlockModal_User
      }
      visit {
        id
      }
      place {
        id
      }
      exposeStatus
      ...UseGoToReviewEdit_ReviewGroup
    }
  `),
}

export default ContextMenuReview
